import { Flex, IconFont, Popover, Text } from '@byecode/ui'
import type { VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { ReactNodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import type { FC } from 'react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { makeMatchPI } from 'ts-adt/MakeADT'

import { USER_DATASOURCE } from '../../../constants'
import { DATE_FORMAT } from '../../../utils/helper'
import { applyBeijingVariableFormat, BEIJING_VARIABLE_DATE_FORMAT } from '../../../utils/shanghaiTime'
import { parseVariableDate } from '../../../utils/variable'
import { VariableTag } from '../../Variable/VariableTag'
import type { VariableExtensionOptions } from './Extension'

const Item = styled.div`
    height: 36px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    &:hover {
        background-color: var(--color-gray-100);
    }
`

interface OptionProps {
    active: boolean
    data: { label: string; value: string }
    onSelect?: (value: string) => void
    fieldFormat?: string
}

const Option = ({ active, data, onSelect, fieldFormat = DATE_FORMAT }: OptionProps) => {
    return (
        <Item
            onClick={() => {
                onSelect?.(data.value)
            }}
        >
            <Flex gap={8} alignItems="center">
                <IconFont style={{ opacity: active ? 1 : 0 }} type="Tick" color="var(--color-main)" />
                <Text size={14}>{data.label}</Text>
            </Flex>
            <Text color="var(--color-gray-400)" size={14}>
                {parseVariableDate(Date.now(), applyBeijingVariableFormat(data.value, fieldFormat) || fieldFormat)}
            </Text>
        </Item>
    )
}

const matchPIVariable = makeMatchPI<'type'>('type')

const DATE_FORMAT_OPTIONS = [
    {
        label: '默认',
        value: ''
    },
    {
        label: '北京时区',
        value: BEIJING_VARIABLE_DATE_FORMAT
    },
    {
        label: '日期时间',
        value: 'yyyy-MM-dd HH:mm'
    },
    {
        label: '日期',
        value: 'yyyy-MM-dd'
    },
    {
        label: '时分',
        value: 'HH:mm'
    },
    {
        label: '年',
        value: 'yyyy'
    },
    {
        label: '月',
        value: 'MM'
    },
    {
        label: '日',
        value: 'dd'
    },
    {
        label: '月日',
        value: 'MM-dd'
    },
    {
        label: '星期',
        value: 'EEEE'
    },
    {
        label: '农历-年',
        value: 'lunar-y'
    },
    {
        label: '农历-月日',
        value: 'lunar-md'
    },
    {
        label: '农历-时辰',
        value: 'lunar-h'
    }
]

function isActive(value: VariableADTvalue, format: string) {
    if (!value) {
        return false
    }
    return matchPIVariable(value)(
        {
            [VariableType.PAGE_DATA_QUERY]: v => (v.pageDataQueryVariable?.format || '') === format,
            [VariableType.USER]: v => (v.userVariable?.format || '') === format,
            [VariableType.VIEW]: v => (v.viewVariable?.format || '') === format,
            [VariableType.SYSTEM]: v => (v.systemVariable?.format || '') === format
        },
        () => false
    )
}

export const VariableBlockComponent: FC<ReactNodeViewProps> = props => {
    const { node, deleteNode, extension, editor, updateAttributes } = props

    const extensionOptions = extension.options as VariableExtensionOptions

    const value = node.attrs.value as VariableADTvalue

    const handleChangeVariableValue = useCallback(
        (value: VariableADTvalue) => {
            updateAttributes({ value })
            // if (editor) {
            //     editor.emit('blur', { editor, event: new FocusEvent('blur'), transaction: editor.state.tr })
            // }
            // extensionOptions.onChangeVariableValue?.(value)
        },
        [updateAttributes]
    )

    const variableInfo = useMemo(() => {
        if (!value) {
            return
        }
        return matchPIVariable(value)(
            {
                [VariableType.PAGE_DATA_QUERY]: v => {
                    const id = v.pageDataQueryVariable?.id
                    const itemId = v.pageDataQueryVariable?.itemId
                    if (!id || !itemId) {
                        return
                    }
                    const schema = extensionOptions.pageVariableSchemas?.find(item => item.id === id)
                    if (!schema) {
                        return
                    }

                    const item = schema.items.find(k => k.id === itemId)
                    if (!item) {
                        return
                    }

                    const ds = extensionOptions.dataSources.find(item => item.id === schema.dsId)
                    if (!ds) {
                        return
                    }

                    const field = ds.schema[item.fieldId]

                    return {
                        dataSource: ds,
                        field,
                        showDateFormat: field?.innerType === 'DATE'
                    }
                },
                [VariableType.USER]: v => {
                    const fieldId = v.userVariable?.fieldId
                    if (!fieldId) {
                        return
                    }
                    const ds = extensionOptions.dataSources.find(item => item.id === USER_DATASOURCE)
                    if (!ds) {
                        return
                    }

                    const field = ds.schema[fieldId]

                    return {
                        dataSource: ds,
                        field,
                        showDateFormat: field?.innerType === 'DATE'
                    }
                },
                [VariableType.VIEW]: v => {
                    const fieldId = v.viewVariable?.fieldId
                    if (!fieldId || !extensionOptions.viewOptions) {
                        return
                    }

                    for (const option of extensionOptions.viewOptions) {
                        const ds = extensionOptions.dataSources.find(d => d.id === option.value)
                        if (!ds) {
                            continue
                        }

                        const field = ds.schema[fieldId]
                        if (!field) {
                            continue
                        }

                        return {
                            dataSource: ds,
                            field,
                            showDateFormat: field?.innerType === 'DATE'
                        }
                    }
                },
                [VariableType.SYSTEM]: () => ({
                    dataSource: undefined,
                    field: undefined,
                    showDateFormat: true
                })
            },
            () => ({
                dataSource: undefined,
                field: undefined,
                showDateFormat: false
            })
        )
    }, [extensionOptions.dataSources, extensionOptions.pageVariableSchemas, extensionOptions.viewOptions, value])

    const label = extensionOptions.renderLabel(node)
    // const tooltip = extensionOptions.renderTooltip?.(node)

    return (
        <NodeViewWrapper as="span" style={{ whiteSpace: 'inherit' }}>
            {editor.isEditable ? (
                <Popover
                    width={260}
                    trapFocus={false}
                    withinPortal
                    disabled={!variableInfo || !variableInfo.showDateFormat}
                    trigger="click"
                >
                    <Popover.Target>
                        <VariableTag
                            withClose
                            onClose={deleteNode}
                            value={value}
                            dateOption={extensionOptions.dateOption}
                            pageOptions={extensionOptions.pageOptions}
                            upstreamOptions={extensionOptions.upstreamOptions}
                            userOption={extensionOptions.userOption}
                            datasourceOption={extensionOptions.datasourceOption}
                            viewOptions={extensionOptions.viewOptions}
                            inputOption={extensionOptions.inputOption}
                            filterOption={extensionOptions.filterOption}
                            formOption={extensionOptions.formOption}
                            eventOption={extensionOptions.eventOption}
                            fieldOptions={extensionOptions.fieldOptions}
                            imageOption={extensionOptions.imageOption}
                            onChangeVariableValue={handleChangeVariableValue}
                            onOpenSelectDataSourceModal={extensionOptions.onOpenSelectDataSourceModal}
                        />
                    </Popover.Target>
                    <Popover.Dropdown>
                        {DATE_FORMAT_OPTIONS.map(item => (
                            <Option
                                key={item.value}
                                fieldFormat={
                                    variableInfo && variableInfo.field?.type === 'date' ? variableInfo.field.date.format : 'yyyy-MM-dd'
                                }
                                data={item}
                                active={isActive(value, item.value)}
                                onSelect={format => {
                                    updateAttributes({
                                        value: matchPIVariable(value)(
                                            {
                                                [VariableType.PAGE_DATA_QUERY]: v => {
                                                    if (!v.pageDataQueryVariable) {
                                                        return v
                                                    }
                                                    return {
                                                        ...v,
                                                        pageDataQueryVariable: {
                                                            ...v.pageDataQueryVariable,
                                                            format
                                                        }
                                                    }
                                                },
                                                [VariableType.USER]: v => {
                                                    if (!v.userVariable) {
                                                        return v
                                                    }
                                                    return {
                                                        ...v,
                                                        userVariable: {
                                                            ...v.userVariable,
                                                            format
                                                        }
                                                    }
                                                },
                                                [VariableType.VIEW]: v => {
                                                    if (!v.viewVariable) {
                                                        return v
                                                    }

                                                    return {
                                                        ...v,
                                                        viewVariable: {
                                                            ...v.viewVariable,
                                                            format
                                                        }
                                                    }
                                                },
                                                [VariableType.SYSTEM]: v => {
                                                    if (!v.systemVariable) {
                                                        return v
                                                    }

                                                    return {
                                                        ...v,
                                                        systemVariable: {
                                                            ...v.systemVariable,
                                                            format
                                                        }
                                                    }
                                                }
                                            },
                                            () => value
                                        )
                                    })
                                }}
                            />
                        ))}
                    </Popover.Dropdown>
                </Popover>
            ) : (
                label
            )}
        </NodeViewWrapper>
    )
}
